import moment from 'moment';
import {
    Serie,
    ThresholdCompare,
    CardConfig,
    SummaryUnit,
    ThresholdCondition,
    SummaryCompareUnit,
    ThresholdStyle,
    ThresholdConditionWithData
} from '../card-configs/config';
import { colors } from '../mdd-styles/colors';
import {
    DashboardCard,
    DashboardCardProfile,
    DashboardCardBaseAttributes,
    Data,
    VisibleSeries,
    SummaryData,
    Parameters,
    CompareMode
} from '@mdd/api';
import { DashboardCardFilters } from 'app/domain/models';
import { isAllString } from 'app/components/shared/checkbox-nested-group/payloadConverter';
import { flatMap, get, cloneDeep } from 'lodash-es';
import { getSerializedProfileRaw } from './getSerializedProfileRaw';
import { compareModeConfigRaw } from '../card-configs/compareModeConfigRaw';
import i18next from 'i18next';

function getGroupsWith(
    groups: Array<Serie[]>,
    property: string,
    valueOrCompareFn: any | ((propValue: any) => boolean)
): Serie[][] {
    const compareFn =
        typeof valueOrCompareFn === 'function' ? valueOrCompareFn : (x) => x === valueOrCompareFn;

    return groups.filter((group) =>
        group.some((serie) => {
            const propValue = get(serie, property);
            return compareFn(propValue);
        })
    );
}

function getSummaryValue(summaryUnit: SummaryUnit | SummaryCompareUnit, summaryData: SummaryData) {
    const data = summaryData.find((d) => d.name === summaryUnit.name) || summaryData[0];
    const point = data && data.values[summaryUnit.row || 0];
    return point && point[summaryUnit.index];
}

const getSeriesGroups = (series: Serie[]): Array<Serie[]> => {
    const groupMap = new Map();
    const seriesWithDefinedGroup = series.filter((serie) => serie.mdd.group !== undefined);
    let usedIndex: Array<number> = [];

    series.forEach((serie, i) => {
        if (
            seriesWithDefinedGroup.find((x) => x.mdd.group === i) ||
            usedIndex.find((x) => x === i)
        ) {
            ++i;
        }
        const group = serie.mdd.group !== undefined ? serie.mdd.group : i;
        usedIndex.push(group);

        if (!groupMap.has(group)) {
            groupMap.set(group, [serie]);
        } else {
            groupMap.get(group).push(serie);
        }
    });

    return Array.from(groupMap.values());
};

function getGroup(series: Serie[], serie: Serie) {
    const groups = getSeriesGroups(series);
    return groups.find((group) => group.some((s) => s.index === serie.index));
}

function getCustomPointWithValue(data, series) {
    return (
        series &&
        series.mdd.getPrimaryValueFromValues &&
        series.mdd.getPrimaryValueFromValues(data.values)
    );
}

export function getLastPointWithValue(data, serie) {
    const reversedData = data.slice().reverse();
    const index = (serie && serie.index + 1) || 1;
    return (
        reversedData &&
        reversedData.find((point) => {
            let value = point[index];
            if (point.date1 && point.value1) {
                value = Number(point.value1);
            }
            if (serie && typeof serie.mdd.computed === 'function') {
                value = serie.mdd.computed(point);
                // This is for computed series like in fresh cow survival card
                // point[index] = value;
            }
            return typeof value !== 'undefined' && value !== null;
        })
    );
}

export const forceUtc = (date?: string) => {
    if (date === undefined) return undefined;
    if (typeof date !== 'string') {
        // console.warn('forcing utc on', date)
        return date;
    }
    const indexOfGMT = date.indexOf('GMT');
    if (indexOfGMT > -1) {
        date = date.slice(0, indexOfGMT);
    }
    // handle year only
    if (date.length === 4) {
        date = `1/1/${date}`;
    }
    date = date.replace(/-/g, '/');
    date = date.replace(/ utc/i, '') + ' UTC';

    return date;
};

function getFirstFuturePointWithValue(data, serie: Serie) {
    const index = serie.index ? serie.index + 1 : 1;
    return (
        data &&
        data.find((point) => {
            const pointDate = moment(forceUtc(point[0])).utc().toISOString();
            const today = moment.utc().toISOString();
            if (pointDate && today && pointDate < today) {
                return false;
            }

            let value = point[index];
            if (serie.mdd.computed) {
                value = serie.mdd.computed(point);
                point[index] = value;
            }

            return typeof value !== 'undefined' && value !== null;
        })
    );
}

function isSmallWidthDevice() {
    return window.innerWidth <= 600;
}

export const getCardName = (dashboardCard: DashboardCard): string => {
    const defaultNameLabelKey = dashboardCard.attributes.defaults.name;
    const customCardName =
        dashboardCard.attributes.name && dashboardCard.attributes.name !== ''
            ? dashboardCard.attributes.name
            : undefined;

    let title = customCardName || i18next.t(defaultNameLabelKey!);
    return title!;
};

export const getName = (
    dashboardCard: DashboardCard,
    cardConfig: CardConfig,
    series: Serie[],
    cardType?: string
): string => {
    const defaultNameLabelKey = dashboardCard.attributes.defaults.name ?? null;
    const clientSideCustomCardName = cardConfig?.mdd?.name ?? undefined;

    const customCardName =
        dashboardCard.attributes.name && dashboardCard.attributes.name !== ''
            ? dashboardCard.attributes.name
            : undefined;

    let title =
        clientSideCustomCardName || customCardName || i18next.t(defaultNameLabelKey!) || undefined;

    if (!title) {
        if (cardType === 'indicator') {
            const cardSummaryQueryRowIndex =
                dashboardCard.attributes.defaults.summaryQueryRowIndex || 0;
            const serieToPresent = series.find(
                (s) => s.mdd.summaryQueryRowIndex === cardSummaryQueryRowIndex
            );

            title = serieToPresent && serieToPresent.name;
        }
        if (cardType === 'separator') {
            title = dashboardCard.name;
        }
        title = title || dashboardCard.card.name;
    }
    return title;
};

export const getIsRegressionSerieVisible = (serVisible, backendVisible, parentSerie: Serie) => {
    if (parentSerie.mdd.hidden) return false;

    const regressionId = parentSerie.regressionSettings!.id!;

    const serValue = serVisible[regressionId];
    if (serValue !== undefined) return serValue;

    const backendValue = backendVisible[regressionId];
    if (backendValue !== undefined) return backendValue;

    if (parentSerie.regressionSettings) {
        if (parentSerie.regressionSettings.visible !== undefined) {
            return parentSerie.regressionSettings.visible;
        }
    }
    return true;
};

export const getIsSerieVisible = (series: Serie[], serVisible, backendVisible, serie: Serie) => {
    if (serie.mdd.hidden) return false;

    const serValue = serVisible[serie.id];
    if (serValue !== undefined) {
        return serValue;
    }

    // serialized group (NOTE: serialized by group is legacy)
    const inGroupIndex = getInGroupIndex(series, serie);
    const groupIndex = serie.mdd.group;
    const serGroupIndexValue = serVisible[groupIndex!];
    if (serGroupIndexValue !== undefined) {
        return serGroupIndexValue === inGroupIndex;
    }

    // backend override
    const backendValue = backendVisible[serie.id];
    if (backendValue !== undefined) {
        return backendValue;
    }

    // series explicit visible value
    if (serie.visible === true || serie.visible === false) {
        return serie.visible;
    }

    // not serialized but in group
    if (groupIndex !== undefined) {
        return inGroupIndex === 0;
    }

    // not serialized and not in group
    return true;
};

export const getSmoothingFactorValueFromProfile = (profile: DashboardCardProfile) => {
    const smoothingFactor =
        profile.parameters && profile.parameters.find((p) => p.name === 'smoothingFactor');
    if (!smoothingFactor) return;
    return smoothingFactor.value as string;
};

export type SimpleParam = { name: string; value: any; transient?: boolean };
export const mergeParameters = (origParams: Array<SimpleParam>, toMerge: Array<SimpleParam>) => {
    return origParams.map((p) => {
        const toMergeParam = toMerge.find((m) => m.name === p.name);
        if (toMergeParam) {
            const newParam = cloneDeep(p);
            newParam.value = toMergeParam.value;
            return newParam;
        }
        return p;
    });
};

export const getCurrentDashboardCardProfile = (
    dashboardCard: DashboardCard,
    cardConfig: CardConfig,
    series: Serie[],
    profileOverride?: DashboardCardProfile,
    options?: DashboardCardFilters
): DashboardCardProfile => {
    const serializedProfile = profileOverride || getSerializedProfileRaw(dashboardCard) || {};

    const backendVisible =
        (dashboardCard.attributes.defaults &&
            dashboardCard.attributes.defaults.series &&
            dashboardCard.attributes.defaults.series.active) ||
        {};

    const serializedVisible = (serializedProfile.series && serializedProfile.series.active) || {};

    const radioCategories = cardConfig.mdd?.radioCategories;
    const selectedRadioCategoryId =
        serializedProfile.selectedRadioCategoryId ||
        cardConfig.mdd.selectedRadioCategoryId ||
        (radioCategories && radioCategories[0] && radioCategories[0].id);

    const visibleSeriesDict: { [id: string]: boolean | undefined } =
        (series.length &&
            series.reduce((acc, serie) => {
                const isVisible = getIsSerieVisible(
                    series,
                    serializedVisible,
                    backendVisible,
                    serie
                );
                const regressionObj = {};
                if (serie.regression && serie.regressionSettings) {
                    const regressionId = serie.regressionSettings.id;
                    let isRegressionVisible = getIsRegressionSerieVisible(
                        serializedVisible,
                        backendVisible,
                        serie
                    );
                    if (serie.regressionSettings.radioCategoryId) {
                        const isRadioCategory =
                            serie.regressionSettings.radioCategoryId === selectedRadioCategoryId;
                        isRegressionVisible = isRadioCategory && isRegressionVisible;
                    }
                    regressionObj[regressionId] = isRegressionVisible;
                }
                return {
                    ...acc,
                    [serie.id]: isVisible,
                    ...regressionObj
                };
            }, {})) ||
        (serializedVisible as any);

    if (dashboardCard.type === 'separator') return {};
    const maxVisibleSeries = cardConfig.mdd.maxVisibleSeries;
    const colorDict = serializedProfile.seriesColorsDict || {};
    let usedColors: string[] = Object.keys(colorDict).map((id) => colorDict[id] as string) || [];
    let availableColors =
        (cardConfig.colors && cardConfig.colors.filter((c) => !usedColors.includes(c as string))) ||
        [];
    const seriesColorsDict =
        maxVisibleSeries === undefined
            ? undefined
            : series.reduce((acc, serie) => {
                  const isVisible = getIsSerieVisible(
                      series,
                      serializedVisible,
                      backendVisible,
                      serie
                  );
                  const isRegressionVisible =
                      serie.regression && serializedVisible[serie.regressionSettings!.id];
                  if (isVisible || isRegressionVisible) {
                      acc[serie.id] = colorDict[serie.id] || availableColors.pop();
                  }
                  return acc;
              }, {});

    const parameters = getDashboardCardParameters(dashboardCard, options).map((p) => {
        const serParams = serializedProfile.parameters;
        if (!serParams) return p;
        const serParam = serParams.find((s) => s.name === p.name);
        return {
            name: (serParam && serParam.name) || p.name,
            value: (serParam && serParam.value) || p.value,
            transient: (serParam && serParam.transient) || p.transient
        };
    });

    const { defaultValues } = dashboardCard.card.parameters;
    const compareMode =
        serializedProfile.compareMode || (defaultValues.compareMode as any as CompareMode); // TODO: fix type

    // inject compare mode series ids
    if (compareMode) {
        compareModeConfigRaw.series!.map((rawSerie) => {
            const rawSerieId = rawSerie.id;
            if (visibleSeriesDict[rawSerieId] === undefined) {
                const serializedValue = serializedVisible[rawSerieId];
                visibleSeriesDict[rawSerieId] =
                    serializedValue !== undefined ? serializedValue : true; // default value
            }
        });
    }

    // NOTE: this should be renamed on backend from headersInfo to headerInfos
    const serializedHeaderInfos = serializedProfile.headersInfo || [];
    // take originals that are in serialized and in order
    const headerInfoSeries = serializedHeaderInfos
        .filter((s) => series.find((o) => s.id === o.id))
        .map((s) => series.find((o) => s.id === o.id)!);
    // add the ones not serialized
    headerInfoSeries.push(...series.filter((o) => !headerInfoSeries.find((h) => h.id === o.id)));
    const headerInfos = cloneDeep(headerInfoSeries).map((serie) => {
        const serIsVisible = visibleSeriesDict[serie.id];
        let isVisible = serIsVisible !== undefined ? serIsVisible : true;
        if (serie.mdd.radioCategoryId) {
            const isRadioCategory = serie.mdd.radioCategoryId === selectedRadioCategoryId;
            isVisible = isRadioCategory && isVisible;
        }
        // TODO: set profile.series[i].visible
        return {
            ...serie,
            visible: isVisible
        };
    });

    return {
        ...serializedProfile,
        selectedRadioCategoryId,
        headersInfo: headerInfos,
        compareMode,
        parameters,
        series: { active: visibleSeriesDict as any },
        seriesColorsDict
    };
};

export const getDashboardCardParameters = (
    dashboardCard: DashboardCard,
    options?: DashboardCardFilters
) => {
    const defaults = dashboardCard.card.parameters.defaultValues as any; // TODO: types
    if (
        dashboardCard.card.parameters.overriddenDefaultValuesForFeatureSet &&
        dashboardCard.card.parameters.overriddenDefaultValuesForFeatureSet[
            dashboardCard.dashboard.featureSetId
        ]
    ) {
        const overriddenDefaults =
            dashboardCard.card.parameters.overriddenDefaultValuesForFeatureSet[
                dashboardCard.dashboard.featureSetId
            ];
        Object.keys(overriddenDefaults).forEach((key) => {
            defaults[key] = overriddenDefaults[key];
        });
    }
    if (defaults.isDistrictCard) return [];

    const serializedProfile = getSerializedProfileRaw(dashboardCard);

    const rawParameters: { [key: string]: any } = {};
    const dynamicDateFilter = ['count', 'offset', 'countLevel', 'offsetLevel'];

    const simpleValues = [
        ...dynamicDateFilter,
        'smoothingFactor',
        'compareOffset',
        'compareOffsetLevel'
    ];

    simpleValues.forEach((d) => {
        const param = getParameter(serializedProfile.parameters, d);
        const p = param !== undefined ? param : defaults[d];
        if (p !== undefined) {
            rawParameters[d] = p;
        }
    });

    options &&
        options.forEach((option) => {
            let optParam = getParameter(serializedProfile.parameters, option.name);

            if (optParam) {
                rawParameters[option.name] = optParam;
            } else {
                const optionName = option.name;
                const defValue =
                    option.filter['defaultValue'] ||
                    option.filter['defaultValues'] ||
                    defaults[optionName];

                if (typeof defValue === 'string') {
                    optParam = defValue;
                } else if (typeof defValue === 'object' && !Array.isArray(defValue)) {
                    const defaultKeys = defValue.defaultKeys && defValue.defaultKeys.split(',');

                    const values = option.filter['values'];

                    if (defaultKeys) {
                        const getValue = (v) => (typeof v === 'string' ? v : v.value);

                        if (Array.isArray(values)) {
                            optParam = values.map(getValue);
                        } else {
                            optParam = Object.keys(values).reduce((acc, key) => {
                                const isKeyIncluded =
                                    isAllString(defaultKeys[0]) || defaultKeys.indexOf(key) > -1;

                                const a = isKeyIncluded ? values[key].map(getValue) : [];
                                return [...acc, ...a];
                            }, [] as string[]);
                        }
                    }
                } else if (typeof defValue === 'object' && Array.isArray(defValue)) {
                    const getValue = (v) => (typeof v === 'string' ? v : v.value);
                    optParam = defValue.map(getValue);
                }
                rawParameters[option.name] = optParam;
            }
        });

    const parameters: DashboardCardBaseAttributes['parameters'] = Object.keys(rawParameters).map(
        (key) => {
            const rawParam = rawParameters[key];
            const value = Array.isArray(rawParam)
                ? rawParam.map((r) => String(r))
                : String(rawParam);
            const opt = options && options.find((opt) => opt.name === key);
            const transient = (opt && opt.filter.transient) || false;
            return {
                name: key,
                value,
                transient
            };
        }
    );

    return parameters;
};

export const getParameter = (parameters: Parameters<string>['parameters'], paramName: string) => {
    const param = parameters && parameters.find((p) => p.name === paramName);
    return param && param.value;
};

export const getSerieLabel = (serie: Serie, isSingular = false) => {
    if (serie.mdd.label) return serie.mdd.label;

    const uom = (!serie.mdd.hideUomInTooltip && serie.mdd.uom) || '';
    const number = Number(serie.name);
    const name = isNaN(number) ? serie.name || '' : '';
    const label = (uom && name && `${uom} ${name}`) || uom || name;

    if (isSingular && label.endsWith('s')) return label.slice(0, -1); // remove ending 's'
    return label;
};

export const getSerieLabelWithoutName = (serie: Serie, isSingular = false) => {
    if (serie.mdd.label) return serie.mdd.label;

    const uom = (!serie.mdd.hideUomInTooltip && serie.mdd.uom) || '';
    const number = Number(serie.name);
    const label = (uom && `${uom}`) || uom;

    if (isSingular && label.endsWith('s')) return label.slice(0, -1); // remove ending 's'
    return label;
};

export const getCategoryColor = (dashboardCard: DashboardCard) => {
    const category = dashboardCard.card.category;
    return colors[category.toLowerCase()];
};

export const getFormattedValue = (cardConfig: CardConfig, series: Serie[], data) => {
    const primaryData = getPrimaryData(cardConfig, series, data);
    const primarySerie = getPrimarySerie(series);

    if (isNaN(primaryData)) {
        return primaryData;
    }
    return primarySerie && primarySerie.mdd.format!(primaryData);
};

export const getDataLevel = (data: Data | undefined) => {
    return (data && data.level) || 'days';
};

export const getDataLevelFromSummaryData = (data: SummaryData | undefined) => {
    return (data && data[0].level) || 'days';
};

export const getAggregationLabel = (smoothingFactor: string, dataLevel: string) => {
    const count = smoothingFactor && smoothingFactor.split('-')[0];
    if (!count || (count && isNaN(count as any))) {
        return '';
    }
    return `${count}-${i18next.t(`date-context:data-level.${dataLevel}`)} ${i18next.t(
        'date-context:data-level.average'
    )}`;
};

export const getCalculationMonthLabel = (data: Data | undefined) => {
    const isCalculationMonthPattern = (value) => new RegExp('^\\d{6}$').test(value);
    const lastCalculationMonthData = data && data.dateInfo && data.dateInfo.calculationMonth;
    const calculationMonthAsString = `${lastCalculationMonthData}`;
    const monthString =
        (isCalculationMonthPattern(calculationMonthAsString) &&
            moment(calculationMonthAsString, 'YYYYMM').format('MMMM').toLowerCase()) ||
        undefined;
    const yearString =
        (isCalculationMonthPattern(calculationMonthAsString) &&
            moment(calculationMonthAsString, 'YYYYMM').format('YYYY')) ||
        undefined;

    return (
        monthString &&
        yearString &&
        `${i18next.t(`date-context:months.short-name.${monthString}`)} ${yearString}`
    );
};

export const getAuditLabel = (
    data: Data | undefined,
    options: DashboardCardFilters | undefined
) => {
    // TODO: tmp default values - need to add translations and move them elsewhere
    const defaultAuditValues = [
        { value: '1', name: 'filter-dropdown:audit.last' },
        { value: '2', name: 'filter-dropdown:audit.previous' }
    ];
    const auditFromData = data && data.dateInfo && data.dateInfo.audit;
    const auditOption = options && options.find((opt) => opt.name === 'audit');
    const auditValues =
        (auditOption &&
            Array.isArray(auditOption.filter['values']) &&
            auditOption.filter['values']) ||
        defaultAuditValues;
    const auditStringValue = auditValues.find(
        (x) => typeof x !== 'string' && x.value === auditFromData
    );

    return (
        (auditStringValue &&
            typeof auditStringValue !== 'string' &&
            i18next.t(auditStringValue.name)) ||
        auditFromData ||
        ''
    );
};

export const getYearLabel = (data: Data | undefined, options: DashboardCardFilters | undefined) => {
    const yearFromData = data && data.dateInfo && data.dateInfo.year;
    const yearValues = options && options.find((opt) => opt.name === 'year');
    const yearStringValue =
        yearValues &&
        Array.isArray(yearValues.filter['values']) &&
        yearValues.filter['values'].find((x) => typeof x !== 'string' && x.value === yearFromData);

    return (
        (yearStringValue && typeof yearStringValue !== 'string' && yearStringValue.name) ||
        yearFromData ||
        ''
    );
};

export const getMonthLabel = (
    data: Data | undefined,
    options: DashboardCardFilters | undefined
) => {
    const monthFromData = data && data.dateInfo && data.dateInfo.month;
    const monthValues = options && options.find((opt) => opt.name === 'month');
    const monthStringValue =
        monthValues &&
        Array.isArray(monthValues.filter['values']) &&
        monthValues.filter['values'].find(
            (x) => typeof x !== 'string' && x.value === monthFromData
        );

    return (
        (monthStringValue && typeof monthStringValue !== 'string' && monthStringValue.name) ||
        monthFromData ||
        ''
    );
};

export const getYearQuarterLabel = (
    data: Data | undefined,
    options: DashboardCardFilters | undefined
) => {
    const yearQuarterFromData = data && data.dateInfo && data.dateInfo.yearQuarter;
    const yearQuarterValues = options && options.find((opt) => opt.name === 'yearQuarter');
    const yearQuarterStringValue =
        yearQuarterValues &&
        Array.isArray(yearQuarterValues.filter['values']) &&
        yearQuarterValues.filter['values'].find(
            (x) => typeof x !== 'string' && x.value === yearQuarterFromData
        );

    return (
        (yearQuarterStringValue &&
            typeof yearQuarterStringValue !== 'string' &&
            yearQuarterStringValue.name) ||
        yearQuarterFromData ||
        ''
    );
};

export const getContext = (
    dashboardCard: DashboardCard,
    cardConfig: CardConfig,
    profile: DashboardCardProfile,
    series: Serie[],
    data: Data | SummaryData
) => {
    const keepContextAsDate = cardConfig.mdd && cardConfig.mdd.keepContextAsDate;
    const date = data && getPrimaryDate(cardConfig, series, data);
    if (!date) return '';
    const dataLevel = getDataLevel(data && data[0]);
    const smoothingFactor = getSmoothingFactorValueFromProfile(profile);
    const agg = getAggregationLabel(smoothingFactor as string, dataLevel);
    const showOnlyPrimaryDataPrefix =
        cardConfig.mdd && cardConfig.mdd.dashboard && cardConfig.mdd.dashboard.hideSecondaryData;
    let primaryDataPrefix = '';
    if (dashboardCard.attributes.defaults.primaryDataPrefix) {
        primaryDataPrefix = i18next.t(`${dashboardCard.attributes.defaults.primaryDataPrefix}`);
    }

    return cardConfig.mdd.getContext(
        agg,
        date,
        dataLevel,
        primaryDataPrefix,
        keepContextAsDate,
        showOnlyPrimaryDataPrefix
    );
};

export const getThresholdStyleThatBreachedThreshold = (
    thresholdStyle: ThresholdStyle,
    val,
    allColumns
) => {
    if (!thresholdStyle) return;

    let ruleToApply;
    for (let i = 0; i < thresholdStyle.rules.length; i++) {
        const rule = thresholdStyle.rules[i];
        const isThresholdBreached = checkIfMatchesCondition(
            val,
            allColumns,
            rule.conditionValidator
        );
        if (isThresholdBreached) {
            ruleToApply = rule;
            break;
        }
    }

    return ruleToApply;
};

export const breachedThreshold = (threshold: ThresholdCompare | undefined, x) => {
    if (!threshold || !x) return;

    switch (threshold.condition) {
        case '<':
            return x < threshold.value;
        case '<=':
            return x <= threshold.value;
        case '==':
            return x == threshold.value;
        case '>=':
            return x >= threshold.value;
        case '>':
            return x > threshold.value;
    }
};

export const getPrimarySerie = (series: Serie[]): Serie | undefined => {
    const groups = getSeriesGroups(series);
    const primaryGroup = getGroupsWith(groups, 'mdd.level', 'primary')[0] || groups[0] || [];
    const primaryVisible = primaryGroup.filter((serie) => serie.visible !== false);
    return (
        primaryVisible.find((serie) => serie.mdd.level === 'primary') ||
        primaryVisible[0] ||
        primaryGroup[0]
    );
};

export const getSecondarySerie = (series: Serie[]): Serie | undefined => {
    const groups = getSeriesGroups(series);
    const secondaryGroup = getGroupsWith(groups, 'mdd.level', 'secondary')[0] || groups[0] || [];
    const secondaryVisible = secondaryGroup.filter((serie) => serie.visible !== false);
    return (
        secondaryVisible.find((serie) => serie.mdd.level === 'secondary') ||
        secondaryVisible[0] ||
        secondaryGroup[0]
    );
};

export const getPrimaryLabel = (series: Serie[]) => {
    const primarySerie = getPrimarySerie(series);
    return primarySerie ? getSerieLabel(primarySerie) : '';
};

export const getSecondaryLabel = (series: Serie[]) => {
    const secondarySerie = getSecondarySerie(series);
    return secondarySerie ? getSerieLabel(secondarySerie) : '';
};

export const checkIfMatchesCondition = (
    diff: number,
    allColumns,
    conditionValidator: (x: any, allColumns: any) => boolean
): boolean => {
    return conditionValidator && conditionValidator(diff, allColumns);
};

export const matchesCondition = (
    diff: number,
    conditionValidator: (x: any) => boolean
): boolean => {
    return conditionValidator && conditionValidator(diff);
};

export const isIndicatorMessage = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData,
    compare: ThresholdCondition
): boolean => {
    const primaryData = getPrimaryData(cardConfig, series, data);
    const compareToData = getCompareToData(cardConfig, data);
    const dataComparator = getDataComparator(cardConfig);
    const diff = compareToData != null ? dataComparator(primaryData, compareToData) : primaryData;
    return matchesCondition(diff, compare.conditionValidator);
};

export const isIndicatorMessageWithData = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData,
    compare: ThresholdConditionWithData
): boolean => {
    const primaryData = getPrimaryData(cardConfig, series, data);
    const compareToData = getCompareToData(cardConfig, data);
    const dataComparator = getDataComparator(cardConfig);
    const diff = compareToData != null ? dataComparator(primaryData, compareToData) : primaryData;
    return checkIfMatchesCondition(diff, data[0].values[0], compare.conditionValidator);
};

export const isIndicatorSuccess = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData
): boolean => {
    const thresholds = cardConfig.mdd.thresholds;
    if (!thresholds || !thresholds.successIf) return false;
    return isIndicatorMessage(cardConfig, series, data, thresholds.successIf);
};

export const isIndicatorWarning = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData
): boolean => {
    const thresholds = cardConfig.mdd.thresholds;
    if (!thresholds || !thresholds.warnIf) return false;
    return isIndicatorMessage(cardConfig, series, data, thresholds.warnIf);
};

export const isIndicatorError = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData
): boolean => {
    const thresholds = cardConfig.mdd.thresholds;
    if (!thresholds || !thresholds.errorIf) return false;
    return isIndicatorMessage(cardConfig, series, data, thresholds.errorIf);
};

export const isIndicatorApex = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData
): boolean => {
    const thresholds = cardConfig.mdd.thresholds;
    if (!thresholds || !thresholds.apexIf) return false;
    return isIndicatorMessageWithData(cardConfig, series, data, thresholds.apexIf);
};

export const isHideIndicatorTrendValue = (
    cardConfig: CardConfig,
    series: Serie[],
    data: SummaryData
): boolean => {
    const thresholds = cardConfig.mdd.thresholds;
    if (!thresholds || !thresholds.hideIndicatorTrendValueIf) return false;
    return isIndicatorMessageWithData(
        cardConfig,
        series,
        data,
        thresholds.hideIndicatorTrendValueIf
    );
};

export const getLastPrimaryDataRow = (series: Serie[], data: Data | SummaryData) => {
    const primarySerie = getPrimarySerie(series);
    const lastPoint = getLastPointWithValue(data.values, primarySerie);
    return lastPoint;
};

export const getPrimaryData = (
    cardConfig: CardConfig,
    series: Serie[],
    data: Data | SummaryData
) => {
    if (cardConfig.mdd && cardConfig.mdd.getLastDateAndValue) {
        const l = cardConfig.mdd.getLastDateAndValue(data, series);
        return l && l.value;
    }

    if (Array.isArray(data)) {
        let primaryDataValue =
            cardConfig.mdd.summary && getSummaryValue(cardConfig.mdd.summary.primary, data);
        if (cardConfig.mdd.summary && cardConfig.mdd.summary.primary.formatter) {
            primaryDataValue = cardConfig.mdd.summary.primary.formatter(primaryDataValue);
        }
        return primaryDataValue;
    } else {
        const primarySerie = getPrimarySerie(series);
        const customPoint = getCustomPointWithValue(data, primarySerie);
        if (customPoint) {
            return customPoint && customPoint[(primarySerie && primarySerie.index! + 1) || 1];
        }

        if (
            cardConfig.mdd.summary &&
            cardConfig.mdd.summary.primary &&
            cardConfig.mdd.summary.primary.dynamic
        ) {
            const calculatedPoint = cardConfig.mdd.summary.primary.dynamic(data.values);
            return calculatedPoint;
        }

        const point =
            primarySerie && primarySerie.mdd.projected
                ? getFirstFuturePointWithValue(data.values, primarySerie)
                : getLastPointWithValue(data.values, primarySerie);

        const primaryDataValue = point && point[(primarySerie && primarySerie.index! + 1) || 1];
        return primaryDataValue !== undefined ? primaryDataValue : point && Number(point.value1);
    }
};

export const getUnformattedPrimaryData = (
    cardConfig: CardConfig,
    series: Serie[],
    data: Data | SummaryData
) => {
    if (cardConfig.mdd && cardConfig.mdd.getLastDateAndValue) {
        const l = cardConfig.mdd.getLastDateAndValue(data, series);
        return l && l.value;
    }

    if (Array.isArray(data)) {
        let primaryDataValue =
            cardConfig.mdd.summary && getSummaryValue(cardConfig.mdd.summary.primary, data);
        return primaryDataValue;
    } else {
        const primarySerie = getPrimarySerie(series);
        const customPoint = getCustomPointWithValue(data, primarySerie);
        if (customPoint) {
            return customPoint && customPoint[(primarySerie && primarySerie.index! + 1) || 1];
        }

        if (
            cardConfig.mdd.summary &&
            cardConfig.mdd.summary.primary &&
            cardConfig.mdd.summary.primary.dynamic
        ) {
            const calculatedPoint = cardConfig.mdd.summary.primary.dynamic(data.values);
            return calculatedPoint;
        }

        const point =
            primarySerie && primarySerie.mdd.projected
                ? getFirstFuturePointWithValue(data.values, primarySerie)
                : getLastPointWithValue(data.values, primarySerie);

        const primaryDataValue = point && point[(primarySerie && primarySerie.index! + 1) || 1];
        return primaryDataValue !== undefined ? primaryDataValue : point && Number(point.value1);
    }
};

export const getPrimaryDate = (cardConfig: CardConfig, series: Serie[], data) => {
    if (data && data.dateInfo && data.dateInfo.year) {
        return new Date(data.dateInfo.year);
    }

    if (data && data.dateInfo && data.dateInfo.calculationMonth) {
        let date = moment(data.dateInfo.calculationMonth, 'YYYYMM').toDate();
        return date.setDate(date.getDate() + 1); //primary data is showing -1 month without this
    }

    if (cardConfig.mdd.getLastDateAndValue) {
        const l = cardConfig.mdd.getLastDateAndValue(data, series);
        return l && l.date;
    }

    if (Array.isArray(data)) {
        const summaryContextUnit = cardConfig.mdd.summary && cardConfig.mdd.summary.context;
        return summaryContextUnit && getSummaryValue(summaryContextUnit, data);
    } else {
        const primarySerie = getPrimarySerie(series);
        const customPoint = getCustomPointWithValue(data, primarySerie);
        if (customPoint) {
            return customPoint && customPoint[0];
        }

        const point =
            primarySerie && primarySerie.mdd.projected
                ? getFirstFuturePointWithValue(data.values, primarySerie)
                : getLastPointWithValue(data.values, primarySerie);

        const primaryDateValue = point && point[0];
        return primaryDateValue !== undefined
            ? primaryDateValue
            : point && moment.utc(point.date1).toString();
    }
};

export const getSecondaryData = (
    cardConfig: CardConfig,
    series: Serie[],
    data: Data | SummaryData
) => {
    if (Array.isArray(data)) {
        const summarySecondaryUnit = cardConfig.mdd.summary && cardConfig.mdd.summary.secondary;
        return summarySecondaryUnit && getSummaryValue(summarySecondaryUnit, data);
    } else {
        const secondarySerie = getSecondarySerie(series);
        if (!secondarySerie) return;
        const point = getLastPointWithValue(data.values, secondarySerie);
        if (!point) return;
        return point[secondarySerie.index! + 1];
    }
};

export const getSecondarySummarySerie = (cardConfig: CardConfig, series: Serie[]) => {
    if (!cardConfig.mdd.summary?.secondary) return;
    const secondarySerieIndex = cardConfig.mdd.summary.secondary.index;
    if (secondarySerieIndex === undefined) return;
    return series[secondarySerieIndex];
};

export const getSecondarySummaryData = (
    cardConfig: CardConfig,
    series: Serie[],
    data: Data | SummaryData
) => {
    if (!cardConfig.mdd.summary?.secondary) return;

    const secondarySerieIndex = cardConfig.mdd.summary.secondary.index;
    if (secondarySerieIndex === undefined) return;

    const secondarySerie = series[secondarySerieIndex];
    if (!secondarySerie) return;

    const point = getLastPointWithValue(data.values, secondarySerie);
    if (!point) return;

    return point[secondarySerieIndex + 1];
};

export const getCompareToData = (cardConfig: CardConfig, data: SummaryData) => {
    const summaryCompareToUnit = cardConfig.mdd.summary && cardConfig.mdd.summary.compareTo;
    const compareToData = summaryCompareToUnit && getSummaryValue(summaryCompareToUnit, data);
    return (compareToData || null) as number;
};

export const getDataComparator = (cardConfig: CardConfig) => {
    const summaryCompareToUnit = cardConfig.mdd.summary && cardConfig.mdd.summary.compareTo;
    const summaryDataComparator = summaryCompareToUnit && summaryCompareToUnit.comparator;
    return summaryDataComparator || ((a, b) => b - a);
};

export const getPrimaryUom = (series: Serie[]): string => {
    const groups = getSeriesGroups(series);
    const yAxis0Groups = getGroupsWith(groups, 'yAxis', (x) => x === undefined || x === 0);
    const visibleYAxis0Serie = flatMap(yAxis0Groups).find((serie) => serie.visible !== false);
    return (visibleYAxis0Serie && get(visibleYAxis0Serie, 'mdd.uom')) || '';
};

export const getSecondaryUom = (series: Serie[]): string => {
    const groups = getSeriesGroups(series);
    const yAxis1Groups = getGroupsWith(groups, 'yAxis', 1);
    const visibleYAxis1Serie = flatMap(yAxis1Groups).find((serie) => serie.visible !== false);
    return (visibleYAxis1Serie && get(visibleYAxis1Serie, 'mdd.uom')) || '';
};

export const getInGroupIndex = (series: Serie[], serie: Serie) => {
    const group = getGroup(series, serie);
    if (!group) return -1;
    return group.findIndex((groupSerie) => groupSerie.index === serie.index);
};

/**
 * Adjusts the first gauge threshold value if it is within a 2% range of the summary data value.
 *
 * This function checks the first threshold value in the `gaugeThresholds` array against the
 * summary data value. If the summary data value is within 2% of the first threshold value,
 * the threshold value is reduced by 20%. This adjustment only occurs if there are more than
 * two unique threshold values and the summary data value is a valid number.
 * This is done to 'hack' how Highcharts displays a gauge when the value equals lower threshold.
 *
 * @param {SummaryData} summaryData - The summary data object, which includes the values to be
 * compared against the gauge thresholds.
 * @param {CardConfig} cardConfig - The card configuration object that contains the gauge
 * thresholds within the `mdd` property.
 * @returns {GaugeThreshold[]} - Modified gauge thresholds if conditions are met, original gauge thresholds otherwise.
 */
export const getLoweredGaugeThreshold = (summaryData: SummaryData, cardConfig: CardConfig) => {
    const gaugeThresholds = cardConfig.mdd?.gaugeThresholds;

    if (!Array.isArray(gaugeThresholds)) {
        return gaugeThresholds;
    }

    const uniqueValues = [...new Set(gaugeThresholds.map((threshold) => threshold.value))];
    const summaryValue = Number(summaryData[0]?.values?.[0]?.[1]);
    const thresholdValue = gaugeThresholds[0]?.value;

    if (
        uniqueValues.length > 2 &&
        !isNaN(summaryValue) &&
        Math.abs(summaryValue - thresholdValue) <= thresholdValue * 0.02
    ) {
        gaugeThresholds[0].value *= 0.8;
    }

    return gaugeThresholds;
};
